import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import WarningIcon from '@mui/icons-material/Warning';
import queryString from 'query-string';
import { Clinic } from '../../common/model/dto/clinic';
import { CreateClinicRequest } from '../../common/model/dto/create-clinic-request';
import { CreateDemoAccountRequest } from '../../common/model/dto/create-demo-account-request';
import { Resource } from '../../common/model/type/resource.enum';
import { FilterModel } from '../../common/model/ui/filter.model';
import { OrderBy } from '../../common/types';
import { getAxiosErrorMessage } from '../../common/utils/helpers';
import { getColumnSortOptions } from '../../common/utils/services/list-filter-utils';
import { getListRange } from '../../common/utils/services/order-list-utils';
import Colours from '../../design-system/colours';
import { useAdminClinicListMutations } from '../../hooks/use-admin-clinic-mutations';
import useAuthorized from '../../hooks/use-authorized';
import { useClinicListColumns } from '../../hooks/use-clinic-list-columns';
import { useListNavigationState } from '../../hooks/use-list-navigation-state';
import { useListQueryParams } from '../../hooks/use-list-query-params';
import { AdminClinicService } from '../../services/admin/admin-clinic.service';
import OvCompactButton from '../UI/atoms/OvCompactButton';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';
import OvClinicTypeahead from '../UI/molecules/OvClinicTypeahead';
import OvCreateClinicDialog from '../UI/molecules/OvCreateClinicDialog';
import OvCreateDemoAccountDialog from '../UI/molecules/OvCreateDemoAccountDialog';
import OvListSortSelect from '../UI/molecules/OvListSortSelect';
import OvListStateMessage from '../UI/molecules/OvListStateMessage';
import OvClinicList from '../UI/organisms/OvClinicList';
import OvListFilterBar from '../UI/organisms/OvListFilterBar';
import OvRecordListPage from '../UI/organisms/OvRecordListPage';

const CLINIC_LIST_LIMIT = 10;
const CLINIC_LIST_FILTERS: FilterModel[] = [];
const DEFAULT_CLINIC_LIST_ORDER: OrderBy = {
  order_by: 'name',
  order_dir: 'asc',
};

const AdminClinicList: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const applicationOperations = useAuthorized(Resource.Application);
  const clinicOperations = useAuthorized(Resource.Clinic);
  const demoAccountOperations = useAuthorized(Resource.DemoAccount);
  const auditActivityOperations = useAuthorized(
    Resource.ClinicAuditLogActivity
  );

  const clinicCreateAllowed =
    clinicOperations.create || applicationOperations.supervise;
  const demoAccountCreateAllowed =
    demoAccountOperations.create || applicationOperations.supervise;
  const auditActivityReadAllowed =
    auditActivityOperations.read || applicationOperations.supervise;

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isCreateDemoAccountDialogOpen, setIsCreateDemoAccountDialogOpen] =
    useState(false);

  const {
    offset,
    orderBy,
    filters,
    changeFilter,
    clearFilters,
    changeOrderBy,
    setOffset,
  } = useListQueryParams(CLINIC_LIST_FILTERS, DEFAULT_CLINIC_LIST_ORDER);

  const getNavigationState = useListNavigationState();
  const sortOptions = getColumnSortOptions(
    useClinicListColumns(getNavigationState),
    t
  );

  const query = queryString.stringify({
    limit: CLINIC_LIST_LIMIT,
    offset,
    order_by: orderBy?.order_by,
    order_dir: orderBy?.order_dir,
  });

  const {
    data: paginatedClinics,
    isPending,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['admin-clinics', query],
    queryFn: () => AdminClinicService.loadClinicsByQuery(query),
    placeholderData: keepPreviousData,
  });

  const clinics = paginatedClinics?.docs ?? [];
  const total = paginatedClinics?.total ?? 0;

  const { createClinic, createDemoClinic } = useAdminClinicListMutations();
  const mutationError = createClinic.error ?? createDemoClinic.error;

  const handleOnSave = (createClinicRequest: CreateClinicRequest) => {
    setIsCreateDialogOpen(false);
    createClinic.mutate(createClinicRequest, {
      onSuccess: (clinic) => navigate(`/clinics/${clinic.id}`),
    });
  };

  const handleOnCreateDemoAccount = (
    createDemoAccountRequest: CreateDemoAccountRequest
  ) => {
    setIsCreateDemoAccountDialogOpen(false);
    createDemoClinic.mutate(createDemoAccountRequest, {
      onSuccess: (response) =>
        navigate(`/clinics/${response?.clinicLocation?.clinic.id}`),
    });
  };

  const selectClinic = (clinic: Clinic | null) => {
    if (clinic?.id) {
      navigate(`/clinics/${clinic.id}`);
    }
  };

  const getResultSummary = () => {
    if (isError) {
      return t('common.listResults.unavailable');
    }

    if (isPending) {
      return t('common.listResults.loading');
    }

    return t('clinics.resultRange', {
      ...getListRange(offset, CLINIC_LIST_LIMIT, total),
      total,
    });
  };

  const renderContent = () => {
    if (isError) {
      return (
        <OvListStateMessage
          isAlert
          icon={<WarningIcon />}
          title={t('clinics.states.errorTitle')}
          description={t('clinics.states.errorDescription')}
        >
          <OvCompactButton onClick={() => refetch()}>
            {t('common.actions.retry')}
          </OvCompactButton>
        </OvListStateMessage>
      );
    }

    if (isPending) {
      return null;
    }

    if (clinics.length > 0) {
      return (
        <OvClinicList
          clinics={clinics}
          orderBy={orderBy?.order_by ?? ''}
          orderDir={orderBy?.order_dir ?? ''}
          onSort={changeOrderBy}
        />
      );
    }

    return (
      <OvListStateMessage
        icon={<LocalHospitalIcon />}
        title={t('clinics.states.emptyTitle')}
        description={
          clinicCreateAllowed
            ? t('clinics.states.emptyDescription')
            : t('clinics.states.emptyReadOnlyDescription')
        }
      />
    );
  };

  return (
    <OvRecordListPage
      title={t('app.subPages.menu.clinics')}
      icon={<LocalHospitalIcon />}
      headerAction={
        <>
          <ClinicSearch
            placeholder={t('common.actions.search')}
            onClinicSelect={selectClinic}
          />
          {clinicCreateAllowed && (
            <OvCompactButton
              icon={<AddCircleIcon />}
              onClick={() => setIsCreateDialogOpen(true)}
            >
              {t('clinics.createNewClinic')}
            </OvCompactButton>
          )}
          {demoAccountCreateAllowed && (
            <OvCompactButton
              icon={<AddCircleIcon />}
              onClick={() => setIsCreateDemoAccountDialogOpen(true)}
            >
              {t('common.actions.createDemoClinic')}
            </OvCompactButton>
          )}
        </>
      }
      filters={
        <OvListFilterBar
          filters={filters}
          onFilterChange={changeFilter}
          onClearFilters={clearFilters}
        >
          <OvListSortSelect
            options={sortOptions}
            orderBy={orderBy?.order_by ?? ''}
            orderDir={orderBy?.order_dir ?? ''}
            onChange={changeOrderBy}
          />
        </OvListFilterBar>
      }
      context={
        <>
          {auditActivityReadAllowed && (
            <InfoSection>
              {t('common.clinicFields.requestCountInfo')}
            </InfoSection>
          )}
          {mutationError && (
            <OvErrorMessage message={getAxiosErrorMessage(mutationError)} />
          )}
        </>
      }
      resultSummary={getResultSummary()}
      total={total}
      limit={CLINIC_LIST_LIMIT}
      offset={offset}
      onPageChange={(_, page) => setOffset((page - 1) * CLINIC_LIST_LIMIT)}
      isBusy={
        isFetching || createClinic.isPending || createDemoClinic.isPending
      }
    >
      {renderContent()}

      <OvCreateClinicDialog
        isOpen={isCreateDialogOpen}
        onCancel={() => setIsCreateDialogOpen(false)}
        onSave={handleOnSave}
      />

      <OvCreateDemoAccountDialog
        isOpen={isCreateDemoAccountDialogOpen}
        onCancel={() => setIsCreateDemoAccountDialogOpen(false)}
        onSave={handleOnCreateDemoAccount}
        title={t('common.actions.createDemoClinic')}
        enableCreatePassword={true}
      />
    </OvRecordListPage>
  );
};

export default AdminClinicList;

const ClinicSearch = styled(OvClinicTypeahead)`
  width: 15rem;
  max-width: 100%;
`;

const InfoSection = styled.div`
  margin-top: 0.75rem;
  padding: 0.5rem;
  border-radius: 0.5rem;
  background-color: ${Colours.LIGHT_BLUE_GRAY};
  color: ${Colours.BLACK};
  font-size: 0.875rem;
`;
